import { useState } from 'react';
import {
  line, Color, LIST_PAGES, NEW_DOCUMENT_ID, Size, paymentPills, recordHref, pageKindLineText, t, type PaymentAnswer, } from '@bms2/shared';
import { Button, DatePicker, Dropdown, NumericInput, Select, Textarea } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { DetailList, DetailRow } from '../../components/composites/DetailList';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { Modal } from '../../components/modals/Modal';
import { ReasonWindow } from '../../components/modals/ReasonWindow';
import { ModalField } from '../../components/modals/ModalShell';
import modalStyles from '../../components/modals/modal.module.css';
import { AccessDenied } from '../../shell/Fallbacks';
import { useStatusResolver } from '../../shell/StatusResolver';
import { PaymentForm } from './PaymentForm';
import { BannerPills } from './BannerPills';
import { DocumentFilesSection } from './DocumentFilesSection';
import { DocumentViewPage, type DocumentLinesDescriptor } from './DocumentViewPage';
import { PAYMENTS_PATH, post, useApi } from './api';
import { usePersonSearch } from '../settings/api';
import { documentPdfPath } from './ViewPdfLink';
import { type ScreenFact } from './PageFacts';
import styles from './DocumentPage.module.css';
import { Money } from '../../components/Money';
import { useAddNew } from './AddNewHost';
import { DocumentNotes, carriedNotes } from './DocumentNotes';
import { useDocumentToday } from '../../shell/ViewerZone';
import { AccountJournalTile } from '../accounting/AccountJournalTile';

/** What the payment's edit mode read for its own apply block: the account's open invoices. */
interface OpenInvoice {
  id: string;
  invoiceNumber: string | null;
  dueDate: string;
  openBalanceCents: number;
  /** 🔨 A written-off invoice is on the list, and says what applying money to it will reverse (decision 0197). */
  status: string;
}

/** The three rails a refund can leave by; a refund off the original names its reason and its authoriser. */
const REFUND_METHODS = ['Gateway', 'Check', 'ACH'] as const;

type OpenModal = 'apply' | 'refund' | 'void' | null;

/**
 * 🔒 The Payment's page (piece 22, the payment's screens; decision 0409): view
 * mode draws the money as it was recorded — the account, the rail, what is
 * applied and what is left — with the invoices it settled and the refunds
 * against it beneath; edit mode is the Record Payment form the `+ New` route
 * opens.
 *
 * 🔒 The PDF view draws the payment's own sheet: the receipt (decision 0515),
 * or, for a payment made by check, the check over the check stock it prints on
 * (T3126; decision 7630). The stock is the screen's alone, and the page says so
 * above the sheet, because the paper in the printer already carries it.
 *
 * 🔨 The page carries no Edit, because a payment is Recorded when it is saved.
 * A mis-applied allocation is dissociated, money given back is a refund, and a
 * payment that never happened is voided.
 */
export function PaymentPage({ paymentId, apiBaseUrl = '' }: { paymentId: string; apiBaseUrl?: string }) {
  const addNew = useAddNew(apiBaseUrl);
  // 🔒 The banner's pills read the shell's one status resolver, as the lists do (decision 0639).
  const resolveStatus = useStatusResolver();
  /**
   * 🔨 `documentPageMode` is not asked here. It answers edit to any address
   * carrying `?mode=edit`, and the form this page holds takes no record id, so
   * that answer would draw an empty Record Payment form over a recorded payment
   * and record a second one on save (found in review, 2026-09-15). A payment has
   * no edit mode at all, so the sentinel id is the whole question.
   */
  const creating = paymentId === NEW_DOCUMENT_ID;
  const page = useApi<PaymentAnswer>(creating ? null : `${PAYMENTS_PATH}/${paymentId}`, apiBaseUrl);
  const [modal, setModal] = useState<OpenModal>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 🔨 The refund's optional reason is the refund form's own, never the Void window's (decision 2870).
  const [refundReason, setRefundReason] = useState('');
  const [applied, setApplied] = useState<Record<string, number>>({});
  const [refundMethod, setRefundMethod] = useState<string>('Check');
  const [refundCents, setRefundCents] = useState<number | null>(null);
  // 🔒 Opens on today in the viewer's own zone, through the hook every document form reads (decision 1030).
  const today = useDocumentToday();
  const [refundedOn, setRefundedOn] = useState(today);
  const [authoriser, setAuthoriser] = useState<{ id: string; label: string } | null>(null);
  const personSearch = usePersonSearch(apiBaseUrl);

  const companyId = page.state.kind === 'ready' ? page.state.data.companyId : null;
  // The apply block reads the same open invoices the edit mode reads, so the two never disagree about what is owed.
  const open = useApi<{ openInvoices: OpenInvoice[] }>(companyId ? `${PAYMENTS_PATH}/defaults?companyId=${encodeURIComponent(companyId)}` : null, apiBaseUrl);

  const close = () => {
    setModal(null);
    setRefundReason('');
    setApplied({});
    setRefundCents(null);
    setAuthoriser(null);
  };

  /**
   * 🔨 `act` deliberately leaves the window open on a refusal so the person can
   * fix what was refused. A shared reason once carried a refused refund's reason
   * into the next Void (found in review, 2026-09-15); the refund's reason is its
   * own now and the Void window holds its own (decision 2870). Opening clears the refund's.
   */
  const openModal = (which: Exclude<OpenModal, null>) => {
    setRefundReason('');
    setModal(which);
  };

  // 🔨 A payment is recorded on its own page and never reopened, so a recorded payment draws its record whatever the address asks for.
  if (creating) return <PaymentForm apiBaseUrl={apiBaseUrl} />;
  if (page.denied) return <AccessDenied reason={page.deniedReason} />;

  const act = async (path: string, body: unknown, ok: string) => {
    setBusy(true);
    const answer = await post<unknown>(`${PAYMENTS_PATH}/${path}`, body, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) {
      setNotice(`!${answer.message}`);
      return;
    }
    close();
    setNotice(ok);
    page.reload();
    open.reload();
  };

  const openInvoices = open.state.kind === 'ready' ? open.state.data.openInvoices : [];

  return (
    <>
    <AsyncStatePanel state={page.state} emptyAction={t('document.notFound')} onRetry={page.reload}>
      {(payment) => {
        const moneyOf = (amount: number) => <Money cents={amount} currencyCode={payment.currencyCode} />;
        const facts: ScreenFact[] = [
          { label: t('payment.fact.account'), value: payment.companyName, route: `/companies/${payment.companyId}` },
          { label: t('payment.fact.direction'), value: payment.direction },
          { label: t('payment.fact.method'), value: payment.method },
          // 🔒 Where the money moved (decision 0434): the payment's own account when Sent, its Deposit's when Received.
          // 🔨 An unbanked receipt says so plainly and raises no warning — recording a cheque before the deposit is ordinary.
          { label: t('payment.bankAccount'), value: payment.bankAccountName ?? t('payment.bankAccount.unbanked') },
          { label: t('payment.fact.paymentDate'), value: payment.paymentDate },
          { label: t('payment.fact.amount'), value: moneyOf(payment.amountCents) },
          { label: t('payment.fact.applied'), value: moneyOf(payment.allocatedCents) },
          { label: t('payment.fact.unapplied'), value: moneyOf(payment.unallocatedCents) },
        ];
        const footer = (
          <>
            {payment.footer?.mayAllocate ? <Button size={Size.Medium} color={Color.Navy} text={[t('payment.action.apply')]} actions={[() => openModal('apply')]} disabled={busy} /> : null}
            {payment.footer?.mayRefund ? <Button size={Size.Medium} text={[t('payment.action.refund')]} actions={[() => openModal('refund')]} disabled={busy} /> : null}
            {payment.footer?.mayVoid ? <Button size={Size.Medium} color={Color.Inactive} text={[t('payment.action.void')]} actions={[() => openModal('void')]} disabled={busy} /> : null}
            {payment.footer?.refusal ? <span className={styles.refusal} data-payment-refusal="true">{payment.footer.refusal}</span> : null}
            {/* 🔨 The payment's own sheet is the receipt: Region 8 lists the invoices it settled (decision 0515). */}
            {/* 🔨 The sheet's own control moved to the action bar above both views, so one document offers it once (decision 0612). */}
          </>
        );
        const applyTotal = Object.values(applied).reduce((sum, value) => sum + value, 0);
        /** 🔒 The invoices this payment settled, as the one read-only table draws every kind's lines (decision 0612). */
        const lines: DocumentLinesDescriptor = {
          title: t('payment.applied.title'),
          rowAttribute: 'data-payment-allocation',
          blockAttribute: 'data-payment-allocations',
          empty: t('payment.applied.none'),
          rows: (payment.allocations ?? []).map((row) => ({
            id: row.id,
            label: row.targetNumber ?? row.targetId,
            // 🔒 A bill or an invoice the payment settled opens its page (T3754), by the one route every list and tile reads.
            route: row.targetType === 'Bill' || row.targetType === 'Invoice' ? recordHref(row.targetType, row.targetId) : null,
            note: row.closedAt ? t('payment.applied.dissociated') : null,
            cells: [row.appliedOn],
            amount: moneyOf(row.amountCents),
            attributes: { 'data-payment-allocation-closed': row.closedAt ? 'true' : 'false' },
            // 🔨 Dissociating closes the row and leaves the payment standing; the invoice owes again (decision 0382).
            action: row.closedAt === null && payment.status !== 'Void' ? (
              <Button
                size={Size.Small}
                color={Color.Destructive}
                text={[t('payment.action.dissociate')]}
                actions={[() => void act(`allocations/${row.id}/dissociate`, {}, t('payment.dissociated'))]}
                disabled={busy}
                confirm={{ title: t('payment.dissociate.title'), consequence: { body: t('payment.dissociate.consequence') }, confirmLabel: t('payment.dissociate.confirm') }}
              />
            ) : undefined,
          })),
          totals: [{ label: t('payment.fact.unapplied'), value: moneyOf(payment.unallocatedCents), attributes: { 'data-payment-unapplied': String(payment.unallocatedCents) } }],
        };
        // 🔒 One notes panel, placed in the Details view and handed to the PDF view's rail; only one view is mounted (T587; decision 1403).
        const carried = carriedNotes(payment);
        const notesPanel = <DocumentNotes recordType="Payment" recordId={payment.id} recordLabel={payment.reference ?? undefined} apiBaseUrl={apiBaseUrl} onError={(message) => setNotice(`!${message}`)} carried={carried} />;
        // 🔒 The files attached to this Payment, one block placed in the Details view (decision 0457; T1071).
        const filesPanel = <DocumentFilesSection recordType="Payment" recordId={payment.id} writeRefusal={payment.writeRefusal ?? null} apiBaseUrl={apiBaseUrl} />;
        // One Account Journal tile, placed in the Details view and handed to the PDF view's rail as its Journal tab (T1070; decision 1662).
        const journalPanel = <AccountJournalTile sourceDocumentType="Payment" sourceDocumentId={payment.id} apiBaseUrl={apiBaseUrl} />;
        return (
          <DocumentViewPage
            notes={carried === null ? null : notesPanel}
            files={filesPanel}
            journal={journalPanel}
            documentType="Payment"
            icon="amountInBankFeed"
            title={payment.reference ?? t('payment.reference.none')}
            subtitle={pageKindLineText({ page: 'document', documentType: 'Payment' })}
            information={facts}
            status={<BannerPills pills={paymentPills(payment, resolveStatus)} attribute="data-payment-pills" />}
            footer={footer}
            footerAttributes={{ 'data-payment-actions': payment.footer?.mayAllocate ? 'apply' : 'none' }}
            pageAttributes={{ 'data-payment': payment.id, 'data-payment-status': payment.status }}
            layout={payment.printLayout ?? null}
            sheet={payment.printSheet ?? null}
            writeRefusal={payment.writeRefusal ?? null}
            ribbon={payment.status}
            pdfPath={documentPdfPath(PAYMENTS_PATH, payment.id)}
            sheetHeader={payment.checkSheet ? <p className={styles.muted} data-payment-check-sheet="true">{t('print.checkStock.screenOnly')}</p> : undefined}
            listRoute={LIST_PAGES.Payment.route}
            apiBaseUrl={apiBaseUrl}
            lines={lines}
            notices={notice ? <AlertBanner tone={notice.startsWith('!') ? 'error' : 'good'} message={notice.replace(/^!/, '')} action={{ label: t('modal.dismiss'), onAction: () => setNotice(null) }} /> : null}
            details={
              <>
                {/* 🔒 What this document posted and where each amount was placed (decision 1075). */}
                {journalPanel}
              {(payment.refunds?.length ?? 0) > 0 ? (
                <section className={styles.block} data-payment-refunds={payment.refunds?.length ?? 0}>
                  <h2 className={styles.heading}>{t('payment.refunds.title')}</h2>
                  <DetailList look="separator">
                    {(payment.refunds ?? []).map((row) => (
                      <DetailRow
                        key={row.id}
                        data-payment-refund={row.id}
                        title={line(row.method, '·', row.status)}
                        detail={row.reason ? row.reason : null}
                        columns={[{ value: row.refundedOn }]}
                      >
                        <span className={styles.rowAmount}>{moneyOf(row.amountCents)}</span>
                      </DetailRow>
                    ))}
                  </DetailList>
                </section>
              ) : null}
              {filesPanel}
              {/* 🔒 The document's notes, the composer at the top of the list (decision 0951; T336). */}
              {notesPanel}
              </>
            }
          >
              {modal === 'apply' ? (
                <Modal
                  open
                  title={t('payment.apply.title')}
                  onCancel={close}
                  dataModal="payment-apply"
                  actions={[
                    {
                      kind: 'primary',
                      label: t('payment.apply.confirm'),
                      onAct: () => void act(`${payment.id}/allocate`, { allocations: Object.entries(applied).filter(([, value]) => value > 0).map(([invoiceId, amountCents]) => ({ invoiceId, amountCents })) }, t('payment.applyDone')),
                      disabled: busy || applyTotal <= 0 || applyTotal > payment.unallocatedCents,
                    },
                    { kind: 'cancel', label: t('modal.cancel'), onAct: close },
                  ]}
                >
                  <p className={modalStyles.fieldLabel}>{t('payment.apply.hint')}</p>
                  {openInvoices.length === 0 ? <p className={modalStyles.fieldLabel}>{t('payment.lines.none')}</p> : null}
                  {openInvoices.map((row) => (
                    <ModalField key={row.id} label={<>{line(row.invoiceNumber ?? row.id, '·')} {moneyOf(row.openBalanceCents)} {line(t('payment.lines.owes'), row.status === 'WrittenOff' ? `· ${t('payment.lines.writtenOff')}` : null)}</>}>
                      <NumericInput size={Size.Medium}
                        label={line(t('payment.lines.apply'), '·', row.invoiceNumber ?? row.id)}
                        labelHidden
                        kind="currency"
                        value={applied[row.id] ?? null}
                        onChange={(next) => setApplied((held) => ({ ...held, [row.id]: next ?? 0 }))}
                        name={`apply-${row.id}`}
                      />
                    </ModalField>
                  ))}
                </Modal>
              ) : null}

              {modal === 'refund' ? (
                <Modal
                  open
                  title={t('payment.refund.title')}
                  onCancel={close}
                  dataModal="payment-refund"
                  actions={[
                    {
                      kind: 'primary',
                      label: t('payment.action.refund'),
                      onAct: () => void act(`${payment.id}/refund`, { method: refundMethod, amountCents: refundCents ?? 0, refundedOn: refundedOn || null, reason: refundReason.trim() || null, authorisedByPersonId: authoriser?.id ?? null }, t('payment.refunded')),
                      disabled: busy || !refundCents || refundCents <= 0,
                    },
                    { kind: 'cancel', label: t('modal.cancel'), onAct: close },
                  ]}
                >
                  {/* 🔨 The server decides whether the rail is the original's; the page says what it will ask for rather than guessing. */}
                  <p className={modalStyles.fieldLabel}>{t('payment.refund.offRail')}</p>
                  <Select size={Size.Medium} label={t('payment.refund.method')} value={refundMethod} onChange={setRefundMethod} options={REFUND_METHODS.map((row) => ({ value: row, label: row }))} dataAttribute={{ 'data-payment-refund-method': 'true' }} required />
                  <ModalField label={t('payment.refund.amount')} required>
                    <NumericInput size={Size.Medium} label={t('payment.refund.amount')} labelHidden kind="currency" value={refundCents} onChange={setRefundCents} name="refundAmount" />
                  </ModalField>
                  <DatePicker size={Size.Small} label={t('payment.refund.on')} value={refundedOn} onChange={setRefundedOn} mode="date" name="refundedOn" />
                  <ModalField label={t('payment.refund.reason')}>
                    <Textarea size={Size.Large} label={t('payment.refund.reason')} labelHidden value={refundReason} onChange={setRefundReason} name="refundReason" />
                  </ModalField>
                  <Dropdown size={Size.Medium} label={t('payment.refund.authoriser')} entityLabel={t('payment.refund.authoriserEntity')} search={personSearch} value={authoriser?.label ?? null} onSelect={(row) => setAuthoriser({ id: row.id, label: row.line1 })} onAddNew={() => addNew.open({ kind: 'employee' }, (row) => setAuthoriser({ id: row.id, label: row.line1 }))} />
                </Modal>
              ) : null}

              {modal === 'void' ? (
                <ReasonWindow
                  open
                  title={t('payment.void.title')}
                  label={t('payment.void.reason')}
                  actLabel={t('payment.action.void')}
                  busy={busy}
                  onCancel={close}
                  refusal={notice?.startsWith('!') ? notice.slice(1) : null}
                  dataModal="payment-void"
                  onAct={(reason) => act(`${payment.id}/void`, { reason }, t('payment.voided'))}
                />
              ) : null}
          </DocumentViewPage>
        );
      }}
    </AsyncStatePanel>
    {addNew.host}
    </>
  );
}
